import { useState } from 'react';

import { appColors, BackrsChip, lottieAnimations } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { CompetenciesFormatter } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/CompetencyNugget';
import { ContactsFormatter } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/ContactNugget';
import { InternshipFormatter } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/InternshipNugget';
import { PostsFormatter } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/PostNugget';
import { ResourcesFormatter } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/ResourceNugget';
import { ScholarshipsFormatter } from '@backrs_ui/grow-ui/src/components/Lubav/nuggets/ScholarshipNugget';
import {
  ResourcesContent,
  RESOURCE_KEY,
  ResourceKey,
  LubavScholarship,
  LubavContact,
  LubavCompetency,
  LubavPost,
  LubavResource,
  LubavInternship,
} from '@backrs_ui/grow-ui/src/models/models';
import { selectResourcesContent } from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  getActiveLubavSession,
  getSessionState,
} from '@backrs_ui/grow-ui/src/redux/lubavSessionState/lubavSessionState.utils';

import { Box, Tabs, Tab, Theme, SxProps } from '@mui/material';
import Lottie from 'lottie-react';

const { officeHours } = appStrings.modules;

const LOADING_TEXT_HEIGHT = 24;

type BoxProps = Readonly<{
  responseData: ResourcesContent;
  tab: ResourceKey;
}>;

const ResourceTab = ({ responseData, tab }: BoxProps) => {
  if (!responseData?.[tab]) {
    return <div></div>;
  }

  const content = responseData[tab].content;

  switch (tab) {
    case RESOURCE_KEY.RESOURCES:
      return <ResourcesFormatter resources={content as LubavResource[]} />;
    case RESOURCE_KEY.EXTERNAL_PROFILES:
      return (
        <Box sx={contactContainerStyles}>
          {(content as LubavContact[]).map((contact: LubavContact) => (
            <ContactsFormatter key={contact.id} contact={contact} type="profile-external" />
          ))}
        </Box>
      );
    case RESOURCE_KEY.COMPETENCIES:
      return <CompetenciesFormatter competencies={content as LubavCompetency[]} />;
    case RESOURCE_KEY.POSTS:
      return <PostsFormatter posts={content as LubavPost[]} />;
    case RESOURCE_KEY.BACKRS:
      return (
        <Box sx={contactContainerStyles}>
          {(content as LubavContact[]).map((contact) => (
            <ContactsFormatter key={contact.id} contact={contact} type="profile-internal" />
          ))}
        </Box>
      );
    case RESOURCE_KEY.STUDENTS:
      return (
        <Box sx={contactContainerStyles}>
          {(content as LubavContact[]).map((contact) => (
            <ContactsFormatter key={contact.id} contact={contact} type="profile-internal" />
          ))}
        </Box>
      );
    case RESOURCE_KEY.SCHOLARSHIPS:
      return <ScholarshipsFormatter scholarships={content as LubavScholarship[]} />;
    case RESOURCE_KEY.INTERNSHIPS:
      return <InternshipFormatter internships={content as LubavInternship[]} />;
  }
};

const BOX_ORDER = ['LEARN', 'PURSUE', 'CONNECT', 'ENGAGE'] as const;

type BoxType = (typeof BOX_ORDER)[number];

type TabConfig = Readonly<{
  label: string;
  key: ResourceKey;
}>;

type BoxConfig = Readonly<{
  title: string;
  tabs: TabConfig[];
}>;

const INFO_BOX_CONFIG: Record<BoxType, BoxConfig> = {
  LEARN: {
    title: officeHours.categories.learn,
    tabs: [
      { label: officeHours.tabs.resources, key: RESOURCE_KEY.RESOURCES },
      { label: officeHours.tabs.profiles, key: RESOURCE_KEY.EXTERNAL_PROFILES },
    ],
  },
  PURSUE: {
    title: officeHours.categories.pursue,
    tabs: [
      { label: officeHours.tabs.scholarships, key: RESOURCE_KEY.SCHOLARSHIPS },
      { label: officeHours.tabs.career, key: RESOURCE_KEY.INTERNSHIPS },
    ],
  },
  CONNECT: {
    title: officeHours.categories.connect,
    tabs: [
      { label: officeHours.tabs.professionals, key: RESOURCE_KEY.BACKRS },
      { label: officeHours.tabs.students, key: RESOURCE_KEY.STUDENTS },
    ],
  },
  ENGAGE: {
    title: officeHours.categories.engage,
    tabs: [
      { label: officeHours.tabs.community, key: RESOURCE_KEY.POSTS },
      { label: officeHours.tabs.competencies, key: RESOURCE_KEY.COMPETENCIES },
    ],
  },
} as const;

export const InfoBox = () => {
  const activeSession = useAppSelector(getActiveLubavSession);

  const { loading: isLoadingResources } = useAppSelector(
    (state) => getSessionState(state, activeSession).boxData,
  );

  const resourcesContent = useAppSelector((state) => selectResourcesContent(state, activeSession));

  const [selectedTab, setSelectedTab] = useState<Record<BoxType, string>>(
    {} as Record<BoxType, string>,
  );

  const hasContent = (boxType: BoxType) =>
    INFO_BOX_CONFIG[boxType].tabs.some(
      (tab) => (resourcesContent?.[tab.key]?.content?.length ?? 0) > 0,
    );

  const availableBoxTypes = BOX_ORDER.filter(hasContent);
  const [selectedBoxType, setSelectedBoxType] = useState<BoxType>(
    availableBoxTypes[0] || BOX_ORDER[0],
  );

  if (!activeSession) {
    return null;
  }

  if (isLoadingResources) {
    return (
      <Box sx={loaderStyles}>
        <Box sx={animationStyles}>
          <Lottie animationData={lottieAnimations.dotsDotting} loop width={250} height={250} />
        </Box>
        <Box sx={loadingTextStyles}>
          {officeHours.loadingMessages.map((message, index) => (
            <Box component="span" key={index} sx={loadingMessageStyles}>
              {message}
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  if (availableBoxTypes.length === 0) {
    return null;
  }

  return (
    <Box sx={containerStyles}>
      <Box sx={chipContainerStyles}>
        {availableBoxTypes.map((boxType) => (
          <BackrsChip
            key={boxType}
            label={INFO_BOX_CONFIG[boxType].title}
            variant={{ type: 'LARGE_TEXT', backgroundColor: 'BLUE' }}
            onClick={() => setSelectedBoxType(boxType)}
            isSelected={selectedBoxType === boxType}
          />
        ))}
      </Box>

      <Box sx={boxContainerStyles}>
        {(() => {
          const currentBox = INFO_BOX_CONFIG[selectedBoxType];
          const availableTabs = currentBox.tabs.filter(
            (tab) => (resourcesContent?.[tab.key]?.content?.length ?? 0) > 0,
          );
          const currentTabValue =
            selectedTab[selectedBoxType] || (availableTabs.length ? availableTabs[0].key : null);

          return (
            <>
              {availableTabs.length > 1 && (
                <Tabs
                  value={currentTabValue}
                  onChange={(_, newValue) =>
                    setSelectedTab({ ...selectedTab, [selectedBoxType]: newValue })
                  }
                  TabIndicatorProps={{ style: { display: 'none' } }}
                  sx={tabStyles}
                >
                  {availableTabs.map((tab) => (
                    <Tab key={tab.key} label={tab.label} value={tab.key} />
                  ))}
                </Tabs>
              )}
              <Box sx={availableTabs.length > 1 ? tabContainerStyles : singleTabContainerStyles}>
                {currentTabValue && ResourceTab && resourcesContent && (
                  <ResourceTab
                    responseData={resourcesContent}
                    tab={currentTabValue as ResourceKey}
                  />
                )}
              </Box>
            </>
          );
        })()}
      </Box>
    </Box>
  );
};

const containerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  height: 'calc(100vh - 104px)',
  padding: '0px 0px 0px 60px',
  width: '100%',
  '@media (max-width: 1015px)': {
    height: 'calc(100vh - 235px)',
  },
  '@media (max-width: 900px)': {
    padding: '20px 0px 0px 0px',
    height: '60%',
    width: 'calc(100vw - 160px)',
  },
} as const;

const boxContainerStyles: SxProps<Theme> = {
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  borderRadius: '8px',
  height: '100%',
  overflow: 'hidden',
  width: 'calc(100% - 50px)',
  backgroundColor: '#FFFFFF',
  '@media (max-width: 900px)': {
    width: '98%',
  },
} as const;

const tabContainerStyles: SxProps<Theme> = {
  overflow: 'auto',
  backgroundColor: '#FFFFFF',
  height: '100%',
  borderRadius: '0px 0px 8px 8px',
  padding: '16px 16px',
  border: '2px solid #000',
  borderTop: 'none',
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
} as const;

const tabStyles: SxProps<Theme> = {
  minHeight: '35px',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    backgroundColor: '#000',
  },
  '.MuiTabs-flexContainer': {
    display: 'flex',
    backgroundColor: `${appColors.color.blue9}50`,
    borderRadius: '8px 8px 0px 0px',
  },
  '.MuiTab-root': {
    fontSize: '10pt',
    textTransform: 'none',
    borderRadius: '8px 8px 0px 0px',
    fontWeight: 'bold',
    color: appColors.color.blue1,
    flex: 1,
    minHeight: '35px',
    height: '35px',
    position: 'relative',
    '&.Mui-selected': {
      color: '#367FFF',
      backgroundColor: '#FFFFFF',
      borderRadius: '8px 8px 0 0',
      border: '2px solid #000',
      borderBottom: 'none',
      zIndex: 1,
    },
    '&:nth-of-type(2)': {
      backgroundColor: `${appColors.color.blue9}85`,
      '&.Mui-selected': {
        color: '#367FFF',
        backgroundColor: '#FFFFFF',
        borderRadius: '8px 8px 0 0',
      },
    },
  },
} as const;

const loaderStyles: SxProps<Theme> = {
  width: '100%',
  margin: 'auto',
  paddingTop: 'calc(40vh - 25%)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  paddingLeft: '34px',
  paddingRight: '34px',
} as const;

const chipContainerStyles: SxProps<Theme> = {
  display: 'flex',
  gap: '10px',
  marginBottom: '20px',
} as const;

const animationStyles: SxProps<Theme> = {
  margin: '0 auto',
} as const;

const loadingTextStyles: SxProps<Theme> = {
  textAlign: 'center',
  color: appColors.color.blue1,
  fontSize: '18px',
  fontWeight: 600,
  marginTop: '20px',
  height: `${LOADING_TEXT_HEIGHT}px`,
  width: '100%',
  overflow: 'hidden',
} as const;

const loadingMessageStyles: SxProps<Theme> = {
  display: 'block',
  whiteSpace: 'nowrap',
  animation: 'cycleMessages 10s infinite',
  lineHeight: `${LOADING_TEXT_HEIGHT}px`,
  width: '100%',
  '@keyframes cycleMessages': {
    '0%, 20%': { transform: 'translateY(0)' },
    '25%, 45%': { transform: `translateY(-${LOADING_TEXT_HEIGHT}px)` },
    '50%, 70%': { transform: `translateY(-${LOADING_TEXT_HEIGHT * 2}px)` },
    '75%, 95%': { transform: `translateY(-${LOADING_TEXT_HEIGHT * 3}px)` },
    '100%': { transform: `translateY(-${LOADING_TEXT_HEIGHT * 4}px)` },
  },
} as const;

const singleTabContainerStyles: SxProps<Theme> = {
  ...tabContainerStyles,
  borderTop: '2px solid #000',
  borderRadius: '8px',
} as const;

const contactContainerStyles: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
} as const;
